import React from "react";
import PropTypes from "prop-types";
import { Box, Slider, Typography } from "@mui/material";
import {
    AccountTreeOutlined,
    GpsFixed,
    TimerOutlined,
} from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import {
    buildUtilitySliderGroups,
    getUtilitySliderValue,
} from "../../utils/csSkillsState";

const LEVEL_LABELS = {
    0: "None",
    1: "Beginner",
    2: "Beginner",
    3: "Basic",
    4: "Intermediate",
    5: "Intermediate",
    6: "Intermediate",
    7: "Advanced",
    8: "Advanced",
    9: "Expert",
    10: "Expert",
};

const SECTION_CARD_SX = {
    display: "flex",
    flexDirection: "column",
    gap: "0.85rem",
    px: { xs: "0.95rem", sm: "1.15rem" },
    py: { xs: "0.95rem", sm: "1.1rem" },
    borderRadius: "12px",
    background: `
        linear-gradient(
            180deg,
            rgba(18,20,22,0.88) 0%,
            rgba(12,14,16,0.94) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    borderLeft: `3px solid ${colors.primary.main}`,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
};

const SCORE_MARKS = [
    { value: 0, label: "0" },
    { value: 2, label: "2" },
    { value: 5, label: "5" },
    { value: 8, label: "8" },
    { value: 10, label: "10" },
];

const SLIDER_SX = {
    color: colors.primary.main,
    height: "28px",
    padding: "10px 0 22px",
    boxSizing: "border-box",
    [`@media ${MOBILE_NAV_MEDIA}`]: {
        height: "38px",
        padding: "10px 0 34px",
    },
    "& .MuiSlider-rail": {
        height: "5px",
        borderRadius: "999px",
        background: `
            linear-gradient(
                180deg,
                rgba(255,255,255,.05),
                rgba(0,0,0,.25)
            )
        `,
        boxShadow: `
            inset 0 2px 3px rgba(255,255,255,.04),
            inset 0 -2px 4px rgba(0,0,0,.45)
        `,
        opacity: 1,
    },
    "& .MuiSlider-track": {
        height: "5px",
        borderRadius: "999px",
        border: "none",
        background: `
            linear-gradient(
                90deg,
                #E7B154 0%,
                #F2BE63 60%,
                #FFD486 100%
            )
        `,
        boxShadow: "0 0 14px rgba(241,178,74,.18)",
    },
    "& .MuiSlider-mark": {
        width: "2px",
        height: "10px",
        top: "50%",
        borderRadius: "3px",
        backgroundColor: "rgba(255,255,255,.28)",
        transform: "translate(-50%, -50%) !important",
    },
    "& .MuiSlider-mark.MuiSlider-markActive": {
        background: "#F2BE63",
        boxShadow: "0 0 8px rgba(241,178,74,.15)",
    },
    "& .MuiSlider-markLabel": {
        fontSize: "0.68rem",
        fontWeight: 600,
        letterSpacing: "0.02em",
        color: "rgba(255,255,255,.65)",
        top: 22,
        transform: "translateX(-50%)",
        [`@media ${MOBILE_NAV_MEDIA}`]: {
            top: 32,
        },
    },
    "& .MuiSlider-markLabel.MuiSlider-markLabelActive": {
        color: "#EBC06D",
        fontWeight: 600,
    },
    "& .MuiSlider-thumb": {
        width: 16,
        height: 16,
        boxSizing: "border-box",
        background: `
            linear-gradient(
                180deg,
                #FFD27C,
                #D88C25
            )
        `,
        border: "2px solid rgba(255,255,255,.18)",
        boxShadow: `
            0 2px 8px rgba(0,0,0,.45),
            0 0 8px rgba(241,178,74,.18)
        `,
        "&::after": {
            content: '""',
            position: "absolute",
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(255,255,255,.18)",
            border: "1px solid rgba(255,255,255,.12)",
            pointerEvents: "none",
        },
        "&:hover, &.Mui-focusVisible, &.Mui-active": {
            boxShadow: `
                0 2px 8px rgba(0,0,0,.45),
                0 0 8px rgba(241,178,74,.18)
            `,
        },
    },
};

function SkillSliderRow({ label, value, onChange, disabled }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "minmax(0, 1fr) 8.1rem",
                    sm: "7.5rem minmax(0, 1fr) 8.1rem",
                },
                alignItems: "center",
                columnGap: { xs: "0.7rem", sm: "1rem" },
                rowGap: "0.35rem",
                width: "100%",
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: colors.text.primary,
                    lineHeight: 1.2,
                    gridColumn: { xs: "1", sm: "1" },
                }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    gridColumn: { xs: "1 / -1", sm: "2" },
                    minWidth: 0,
                    px: { xs: 0, sm: "0.15rem" },
                }}
            >
                <Slider
                    value={value}
                    onChange={(_, next) => onChange(next)}
                    min={0}
                    max={10}
                    step={1}
                    marks={SCORE_MARKS}
                    disabled={disabled}
                    aria-label={label}
                    sx={SLIDER_SX}
                />
            </Box>
            <Box
                sx={{
                    justifySelf: "end",
                    gridColumn: { xs: "2", sm: "3" },
                    gridRow: { xs: "1", sm: "auto" },
                    width: "8.1rem",
                    minWidth: "8.1rem",
                    boxSizing: "border-box",
                    px: "0.45rem",
                    py: "0.22rem",
                    borderRadius: "999px",
                    background: "rgba(232,163,61,0.12)",
                    border: "1px solid rgba(232,163,61,0.22)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                        color: colors.primary.main,
                        lineHeight: 1.2,
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                    }}
                >
                    {value}/10 — {LEVEL_LABELS[value] || "None"}
                </Typography>
            </Box>
        </Box>
    );
}

SkillSliderRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

function SectionCard({ title, icon: Icon, children }) {
    return (
        <Box sx={SECTION_CARD_SX}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.45rem",
                }}
            >
                <Icon
                    sx={{
                        fontSize: "1.05rem",
                        color: colors.primary.main,
                    }}
                />
                <Typography
                    sx={{
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: colors.primary.main,
                        letterSpacing: "0.02em",
                    }}
                >
                    {title}
                </Typography>
            </Box>
            {children}
        </Box>
    );
}

SectionCard.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    children: PropTypes.node,
};

export default function ProfileLoadoutSliders({
    template,
    mapData,
    onUtilityGroupChange,
    onStratChange,
    onRoleChange,
    disabled = false,
}) {
    const utilityGroups = buildUtilitySliderGroups(template?.utilities);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                width: "100%",
            }}
        >
            <SectionCard title="Utility Knowledge" icon={TimerOutlined}>
                {utilityGroups.map((group) => (
                    <SkillSliderRow
                        key={group.keys.join("-")}
                        label={group.label}
                        value={getUtilitySliderValue(mapData, group.keys)}
                        onChange={(value) => onUtilityGroupChange(group.keys, value)}
                        disabled={disabled}
                    />
                ))}
            </SectionCard>

            <SectionCard title="Strat Knowledge" icon={AccountTreeOutlined}>
                <SkillSliderRow
                    label="Strategy & Executes"
                    value={mapData.stratKnowledge || 0}
                    onChange={onStratChange}
                    disabled={disabled}
                />
            </SectionCard>

            <SectionCard title="Role Proficiency" icon={GpsFixed}>
                {(template?.roles || []).map((role) => (
                    <SkillSliderRow
                        key={role.key}
                        label={role.display}
                        value={mapData.roles?.[role.key] || 0}
                        onChange={(value) => onRoleChange(role.key, value)}
                        disabled={disabled}
                    />
                ))}
            </SectionCard>
        </Box>
    );
}

ProfileLoadoutSliders.propTypes = {
    template: PropTypes.object,
    mapData: PropTypes.object,
    onUtilityGroupChange: PropTypes.func.isRequired,
    onStratChange: PropTypes.func.isRequired,
    onRoleChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};
